import { useEffect, useRef, useState } from 'react';
import { cn } from '../../../../../../lib/utils';
import { primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../../../styling/stylingHelper';
import {
  recheckAICheckerSafeSkips,
  recheckAICheckerArchives,
  fetchAICheckerMissingCategorizations,
  fixAICheckerMissingCategorizations,
} from '../../../../../../services/admin/adminService';
import { FileArchive, History, Layers, MoreVertical, RotateCw, Scale, ShieldCheck, Tags } from 'lucide-react';
import type { TFn } from '../../types';

/**
 * Platform groups per "Add missing categorizations" run. Each group can pay for AI on its new
 * product names, so an unbounded run would outlive the HTTP timeout. The action is idempotent —
 * only lines still missing a mainCategory are touched — so the admin just re-clicks until the
 * count next to the item reaches 0.
 */
const MISSING_CATEGORIZATION_BATCH = 5;

export type ExtraPanel = 'backfill' | 'process' | 'candidates' | 'qtyRecheck';

/**
 * "More" overflow menu for the AI Checker header — keeps the toolbar to a few main buttons. Holds the
 * AI-free "Recheck skips" action (runs inline, result shown in the menu) and opens the three backfill
 * modals, whose panels are rendered controlled by the parent so they survive the menu closing.
 */
export function AICheckerExtraMenu({ account, startDate, endDate, onDone, onOpenPanel, t }: {
  account: string; startDate: string; endDate: string; onDone: () => void;
  onOpenPanel: (panel: ExtraPanel) => void; t: TFn;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  // Unclassified product lines, loaded when the menu opens so the item can show what a run would
  // cost before anyone clicks it. null = not loaded (or the read failed) — the item stays usable.
  const [missingCategorizations, setMissingCategorizations] = useState<number | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Read-only and AI-free, so it is safe to fire on every open.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchAICheckerMissingCategorizations()
      .then(r => { if (!cancelled) setMissingCategorizations(r.totalLines); })
      .catch(() => { if (!cancelled) setMissingCategorizations(null); });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      setOpen(false);
      setMsg(null);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); setMsg(null); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const runRecheck = async () => {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await recheckAICheckerSafeSkips(account, startDate, endDate);
      setMsg(t('adminTesting.review.recheckSkipsDone', { updated: r.updated, checked: r.checked }));
      if (r.updated > 0) onDone();
    } catch (e: any) {
      setMsg(e.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const runArchiveRecheck = async () => {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await recheckAICheckerArchives(account, startDate, endDate);
      setMsg(t('adminTesting.review.recheckArchivesDone', { flagged: r.flagged, checked: r.checked }));
      if (r.flagged > 0) onDone();
    } catch (e: any) {
      setMsg(e.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const runMissingCategorizations = async () => {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await fixAICheckerMissingCategorizations({ limit: MISSING_CATEGORIZATION_BATCH });
      setMsg(t('adminTesting.review.missingCategorizationsDone', {
        processed: r.processed,
        orders: r.updatedOrderhistoryRows,
        facturen: r.updatedFactuurRows,
      }));
      // Refresh the badge so a second run shows what is genuinely left.
      const stats = await fetchAICheckerMissingCategorizations().catch(() => null);
      setMissingCategorizations(stats?.totalLines ?? null);
      if (r.processed > 0) onDone();
    } catch (e: any) {
      setMsg(e.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const openPanel = (panel: ExtraPanel) => {
    setOpen(false);
    setMsg(null);
    onOpenPanel(panel);
  };

  const itemCls = cn('w-full flex items-center gap-2 px-3 py-2 rounded-md text-left text-xs transition-colors disabled:opacity-50',
    'hover:bg-gray-100 dark:hover:bg-gray-700', primaryText);

  return (
    <div ref={rootRef} className="relative">
      <button
        onClick={() => { setOpen((o) => !o); setMsg(null); }}
        title={t('adminTesting.review.moreTools')}
        className={cn('inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 whitespace-nowrap', primaryText, smallTextSize, semiBoldText, open && 'bg-gray-50 dark:bg-gray-600')}
      >
        <MoreVertical className="w-4 h-4 shrink-0" />
        {t('adminTesting.review.moreTools')}
      </button>

      {open && (
        <div className={cn('absolute right-0 top-full mt-1 z-50 w-64 p-1.5 rounded-lg border shadow-lg', 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800')}>
          <button onClick={runRecheck} disabled={busy} title={t('adminTesting.review.recheckSkipsTitle')} className={itemCls}>
            <ShieldCheck className="w-3.5 h-3.5 shrink-0 text-purple-600 dark:text-purple-400" />
            <span>{busy ? '…' : t('adminTesting.review.recheckSkips')}</span>
          </button>
          <button onClick={runArchiveRecheck} disabled={busy} title={t('adminTesting.review.recheckArchivesTitle')} className={itemCls}>
            <FileArchive className="w-3.5 h-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
            <span>{busy ? '…' : t('adminTesting.review.recheckArchives')}</span>
          </button>
          <button onClick={runMissingCategorizations} disabled={busy} title={t('adminTesting.review.missingCategorizationsTitle')} className={itemCls}>
            <Tags className="w-3.5 h-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
            <span>{busy ? '…' : t('adminTesting.review.missingCategorizations')}</span>
            {missingCategorizations !== null && (
              <span className={cn('ml-auto shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold',
                missingCategorizations > 0
                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                  : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400')}>
                {missingCategorizations}
              </span>
            )}
          </button>
          {msg && <div className={cn(secondaryText, 'px-3 py-1 text-xs')}>{msg}</div>}
          <button onClick={() => openPanel('qtyRecheck')} title={t('adminTesting.review.qtyRecheckTitle')} className={itemCls}>
            <Scale className="w-3.5 h-3.5 shrink-0 text-purple-600 dark:text-purple-400" />
            <span>{t('adminTesting.review.qtyRecheckRun')}</span>
          </button>
          <button onClick={() => openPanel('backfill')} className={itemCls}>
            <History className="w-3.5 h-3.5 shrink-0 text-gray-500 dark:text-gray-400" />
            <span>{t('adminTesting.review.backfillRun')}</span>
          </button>
          <button onClick={() => openPanel('process')} className={itemCls}>
            <RotateCw className="w-3.5 h-3.5 shrink-0 text-gray-500 dark:text-gray-400" />
            <span>{t('adminTesting.review.backprocessRun')}</span>
          </button>
          <button onClick={() => openPanel('candidates')} className={itemCls}>
            <Layers className="w-3.5 h-3.5 shrink-0 text-gray-500 dark:text-gray-400" />
            <span>{t('adminTesting.review.candidatesRun')}</span>
          </button>
        </div>
      )}
    </div>
  );
}
